import {
  createApiFactory,
  createPlugin,
  createRoutableExtension,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';

import {
  registerComponentRouteRef,
  rootRouteRef,
  scaffolderTaskRouteRef,
  scaffolderTemplatesRouteRef,
  selectedTemplateRouteRef,
  viewTechDocRouteRef,
} from './routes';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponent,
  scaffolderApiRef,
} from '@backstage/plugin-scaffolder-react';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import {
  MyGroupsPicker,
  MyGroupsPickerSchema,
} from './components/fields/MyGroupsPicker/MyGroupsPicker';
import {
  OwnedEntityPicker,
  OwnedEntityPickerSchema,
} from './components/fields/OwnedEntityPicker/OwnedEntityPicker';
import {
  EntityTagsPicker,
  EntityTagsPickerSchema,
} from './components/fields/EntityTagsPicker/EntityTagsPicker';
import {
  OwnerPicker,
  OwnerPickerSchema,
} from './components/fields/OwnerPicker/OwnerPicker';
import {
  RepoUrlPicker,
  RepoUrlPickerSchema,
} from './components/fields/RepoUrlPicker/RepoUrlPicker';
import {
  AgentNameValidator,
  AgentNameValidatorSchemaFinal,
  AgentNameValidatorUiOptions,
  EntityNameValidator,
  EntityNameValidatorSchemaFinal,
  EntityNameValidatorUiOptions,
  DecisionCard,
  DecisionCardSchemaFinal,
  DecisionCardUiOptions,
  FeatureFlaggedAlertField,
  FeatureFlaggedAlertSchema,
  FeatureFlaggedAlertUiOptions,
  HorizontalInputGroupArrayCard,
  HorizontalInputGroupArrayCardSchema,
  HorizontalInputGroupArrayCardUiOptions,
  DropdownValueArrayCard,
  DropdownValueArrayCardSchema,
  DropdownValueArrayCardUiOptions,
  InfoAlertField,
  InfoAlertSchema,
  InfoAlertUiOptions,
  InfoBannerField,
  InfoBannerSchema,
  InfoBannerUiOptions,
  InputGroupArrayCard,
  InputGroupArrayCardSchema,
  InputGroupArrayCardUiOptions,
  InputGroupArrayCheckboxCard,
  InputGroupArrayCheckboxCardSchema,
  InputGroupArrayCheckboxCardUiOptions,
  InputGroupCard,
  InputGroupCardSchemaFinal,
  InputGroupCardUiOptions,
  InputGroupCheckboxCard,
  InputGroupCheckboxCardFieldsFirst,
  InputGroupCheckboxCardFieldsFirstSchemaFinal,
  InputGroupCheckboxCardFieldsFirstUiOptions,
  InputGroupCheckboxCardSchemaFinal,
  InputGroupCheckboxCardUiOptions,
  MultiselectDropdown,
  MultiselectDropdownSchema,
  MultiselectDropdownUiOptions,
  repoPickerValidation,
  TermsCheckbox,
  TermsCheckboxUiOptions,
  UrlPrefixValidator,
  UrlPrefixValidatorSchemaFinal,
  UrlPrefixValidatorUiOptions,
  CostCenterChecker,
  CostCenterCheckerSchemaFinal,
  CostCenterCheckerUiOptions,
} from './components';
import {
  EntityNamePicker,
  EntityNamePickerSchema,
} from './components/fields/EntityNamePicker/EntityNamePicker';
import { entityNamePickerValidation } from './components/fields/EntityNamePicker';
import {
  EntityPicker,
  EntityPickerSchema,
} from './components/fields/EntityPicker/EntityPicker';
import { ScaffolderClient } from './api';
import { formFieldsApiRef } from '@backstage/plugin-scaffolder-react/alpha';
import {
  RepositoryOnboarding,
  RepositoryOnboardingSchema,
} from './components/fields/RepositoryOnboarding';

export const scaffolderPlugin = createPlugin({
  id: 'scaffolder',
  apis: [
    createApiFactory({
      api: scaffolderApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        scmIntegrationsApi: scmIntegrationsApiRef,
        fetchApi: fetchApiRef,
        identityApi: identityApiRef,
      },
      factory: ({ discoveryApi, scmIntegrationsApi, fetchApi, identityApi }) =>
        new ScaffolderClient({
          discoveryApi,
          scmIntegrationsApi,
          fetchApi,
          identityApi,
        }),
    }),
    createApiFactory({
      api: formFieldsApiRef,
      deps: {},
      factory: () => ({ loadFormFields: async () => [] }),
    }),
  ],
  routes: {
    root: rootRouteRef,
    templates: scaffolderTemplatesRouteRef,
    selectedTemplate: selectedTemplateRouteRef,
    ongoingTask: scaffolderTaskRouteRef,
  },
  externalRoutes: {
    registerComponent: registerComponentRouteRef,
    viewTechDoc: viewTechDocRouteRef,
  },
});

/**
 * A field extension for selecting an Entity that exists in the Catalog.
 *
 * @public
 */
export const EntityPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: EntityPicker,
    name: 'EntityPicker',
    schema: EntityPickerSchema,
  }),
);

/**
 * The field extension for selecting a name for a new Entity in the Catalog.
 *
 * @public
 */
export const EntityNamePickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: EntityNamePicker,
    name: 'EntityNamePicker',
    validation: entityNamePickerValidation,
    schema: EntityNamePickerSchema,
  }),
);

/**
 * The field extension which provides the ability to select a RepositoryUrl.
 * Currently, this is an encoded URL that looks something like the following `github.com?repo=myRepoName&owner=backstage`.
 *
 * @public
 */
export const RepoUrlPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: RepoUrlPicker,
    name: 'RepoUrlPicker',
    validation: repoPickerValidation,
    schema: RepoUrlPickerSchema,
  }),
);

/**
 * A field extension for picking users and groups out of the Catalog.
 *
 * @public
 */
export const OwnerPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: OwnerPicker,
    name: 'OwnerPicker',
    schema: OwnerPickerSchema,
  }),
);

/**
 * A field extension for picking groups a user belongs to out of the catalog.
 *
 * @public
 */
export const MyGroupsPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: MyGroupsPicker,
    name: 'MyGroupsPicker',
    schema: MyGroupsPickerSchema,
  }),
);

/**
 * A field extension to show all the Entities that are owned by the current logged-in User for use in templates.
 *
 * @public
 */
export const OwnedEntityPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: OwnedEntityPicker,
    name: 'OwnedEntityPicker',
    schema: OwnedEntityPickerSchema,
  }),
);

/**
 * EntityTagsPickerFieldExtension
 * @public
 */
export const EntityTagsPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: EntityTagsPicker,
    name: 'EntityTagsPicker',
    schema: EntityTagsPickerSchema,
  }),
);

/**
 * A field extension that provides a card layout for grouping related input fields together.
 * This component helps organize form fields into a visually distinct section with an optional title.
 *
 * @public
 */
export const InputGroupCardExtension: FieldExtensionComponent<
  string,
  InputGroupCardUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: InputGroupCard,
    name: 'InputGroupCard',
    schema: InputGroupCardSchemaFinal,
  }),
);

/**
 * A field extension that provides a dropdown with multi-select capabilities.
 * Allows users to select multiple items from a predefined list of options.
 * The selected items are returned as an array of strings.
 *
 * @public
 */
export const MultiselectDropdownFieldExtension: FieldExtensionComponent<
  string,
  MultiselectDropdownUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: MultiselectDropdown,
    name: 'MultiselectDropdown',
    schema: MultiselectDropdownSchema,
  }),
);

/**
 * A field extension that provides an array of input group cards.
 * Useful for creating dynamic forms where users can add multiple instances
 * of a group of related fields, such as key-value pairs or contact information.
 *
 * @public
 */
export const InputGroupArrayCardFieldExtension: FieldExtensionComponent<
  object,
  InputGroupArrayCardUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: InputGroupArrayCard,
    name: 'InputGroupArrayCard',
    schema: InputGroupArrayCardSchema,
  }),
);

/**
 * A field extension that provides a horizontally rendered array of generic input groups.
 * Useful for endpoint definitions or any compact row-based object arrays.
 *
 * @public
 */
export const HorizontalInputGroupArrayCardFieldExtension: FieldExtensionComponent<
  object,
  HorizontalInputGroupArrayCardUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: HorizontalInputGroupArrayCard,
    name: 'HorizontalInputGroupArrayCard',
    schema: HorizontalInputGroupArrayCardSchema,
  }),
);

export const DropdownValueArrayCardFieldExtension: FieldExtensionComponent<
  object,
  DropdownValueArrayCardUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: DropdownValueArrayCard,
    name: 'DropdownValueArrayCard',
    schema: DropdownValueArrayCardSchema,
  }),
);

/**
 * A field extension that provides a checkbox-gated input card.
 * Displays an info alert when a value is already configured and reveals
 * an input field when the user checks the checkbox to replace it.
 *
 * @public
 */
export const InputGroupCheckboxCardExtension: FieldExtensionComponent<
  object,
  InputGroupCheckboxCardUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: InputGroupCheckboxCard,
    name: 'InputGroupCheckboxCard',
    schema: InputGroupCheckboxCardSchemaFinal,
  }),
);

export const InputGroupCheckboxCardFieldsFirstExtension: FieldExtensionComponent<
  object,
  InputGroupCheckboxCardFieldsFirstUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: InputGroupCheckboxCardFieldsFirst,
    name: 'InputGroupCheckboxCardFieldsFirst',
    schema: InputGroupCheckboxCardFieldsFirstSchemaFinal,
  }),
);

/**
 * A field extension that provides an array of input group cards with a checkbox option.
 * Useful for creating dynamic forms where users can add multiple instances
 * of a group of related fields, such as key-value pairs or contact information.
 *
 * @public
 */
export const InputGroupArrayCheckboxCardFieldExtension: FieldExtensionComponent<
  object,
  InputGroupArrayCheckboxCardUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: InputGroupArrayCheckboxCard,
    name: 'InputGroupArrayCheckboxCard',
    schema: InputGroupArrayCheckboxCardSchema,
  }),
);

/**
 * A field extension that provides an info alert box.
 * Useful for displaying informational messages to users within a form.
 *
 * @public
 */
export const InfoAlertFieldExtension: FieldExtensionComponent<
  object,
  InfoAlertUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: InfoAlertField,
    name: 'InfoAlertField',
    schema: InfoAlertSchema,
  }),
);

/**
 * A field extension that renders a configurable MUI Alert banner in scaffolder forms.
 * Configure via ui:options in the template: severity, title, description, linkText, linkUrl.
 *
 * @public
 */
export const InfoBannerFieldExtension: FieldExtensionComponent<
  string,
  InfoBannerUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: InfoBannerField,
    name: 'InfoBanner',
    schema: InfoBannerSchema,
  }),
);

/**
 * A field extension that displays an alert banner conditionally based on a platform feature flag.
 * Useful for showing temporary notices (e.g., paused services) that can be toggled via config.
 *
 * @public
 */
export const FeatureFlaggedAlertFieldExtension: FieldExtensionComponent<
  string,
  FeatureFlaggedAlertUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: FeatureFlaggedAlertField,
    name: 'FeatureFlaggedAlertField',
    schema: FeatureFlaggedAlertSchema,
  }),
);

/**
 * A field extension that provides a checkbox specifically designed for terms and conditions acceptance.
 * This component can be used to require users to accept terms before proceeding with form submission.
 *
 * @public
 */
export const TermsCheckboxFieldExtension: FieldExtensionComponent<
  object,
  TermsCheckboxUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TermsCheckbox',
    component: TermsCheckbox,
  }),
);

/**
 * A field extension for repository onboarding that accepts a string input
 * and returns it as an object with a value property.
 *
 * @public
 */
export const RepositoryOnboardingFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'RepositoryOnboarding',
    component: RepositoryOnboarding,
    schema: RepositoryOnboardingSchema,
  }),
);

/**
 * A field extension for validating agent names against existing agents in Agent Garden.
 * Performs case-insensitive validation and provides real-time feedback on name availability.
 *
 * @public
 */
export const AgentNameValidatorFieldExtension: FieldExtensionComponent<
  string,
  AgentNameValidatorUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: AgentNameValidator,
    name: 'AgentNameValidator',
    schema: AgentNameValidatorSchemaFinal,
  }),
);

/**
 * A field extension for validating entity names against existing entities.
 * Performs case-insensitive validation and provides real-time feedback on name availability.
 *
 * @public
 */
export const EntityNameValidatorFieldExtension: FieldExtensionComponent<
  string,
  EntityNameValidatorUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: EntityNameValidator,
    name: 'EntityNameValidator',
    schema: EntityNameValidatorSchemaFinal,
  }),
);

/**
 * A field extension that provides a decision card with radio buttons and dependent text fields.
 * When the negative option is selected, dependent text fields are automatically disabled.
 *
 * @public
 */
export const DecisionCardFieldExtension: FieldExtensionComponent<
  object,
  DecisionCardUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: DecisionCard,
    name: 'DecisionCard',
    schema: DecisionCardSchemaFinal,
  }),
);

/**
 * A field extension for validating an n8n order's URL prefix: it enforces the
 * lowercase/digits/dashes-only format and checks uniqueness against the n8n
 * Instance Manager API, giving real-time feedback while the user types.
 *
 * @public
 */
export const UrlPrefixValidatorFieldExtension: FieldExtensionComponent<
  string,
  UrlPrefixValidatorUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: UrlPrefixValidator,
    name: 'UrlPrefixValidator',
    schema: UrlPrefixValidatorSchemaFinal,
  }),
);

/**
 * A field extension for the cost center input on n8n regular instance order and
 * edit forms. Checks the n8n Instance Manager API for existing orders with the
 * same cost center and displays a non-blocking informational warning if found.
 *
 * @public
 */
export const CostCenterCheckerFieldExtension: FieldExtensionComponent<
  string,
  CostCenterCheckerUiOptions
> = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: CostCenterChecker,
    name: 'CostCenterChecker',
    schema: CostCenterCheckerSchemaFinal,
  }),
);

/**
 * @alpha
 * The Router and main entrypoint to the Alpha Scaffolder plugin.
 */
export const NextScaffolderPage = scaffolderPlugin.provide(
  createRoutableExtension({
    name: 'NextScaffolderPage',
    component: () =>
      import('./pages/ScaffolderIndex').then(m => m.ScaffolderIndex),
    mountPoint: rootRouteRef,
  }),
);
